import { useState, useCallback } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';
import { useGetFirstAvailableSiteDomain } from '@atlassian/gic-anywhere';

import { useSessionData } from '@confluence/session-data';

import { getPersistedOverrideCloudId } from '../utils/localStorageHelpers';

export type UseGetAvailableSitesOptions = {
	onSitesLoaded?: (sites: AvailableSite[]) => void;
	onError?: (error: Error) => void;
};

export type UseGetAvailableSitesResult = {
	availableSites: AvailableSite[];
	isLoading: boolean;
	error: Error | null;
	selectedAvailableSite: AvailableSite | null;
};

export const useGetAvailableSitesForSingleIssueCreateForm = (
	props: UseGetAvailableSitesOptions = {},
): UseGetAvailableSitesResult | void => {
	const { onSitesLoaded, onError } = props;
	const { cloudId: sessionCloudId } = useSessionData();
	const persistedOverrideCloudId = getPersistedOverrideCloudId();
	const overrideCloudIdForSitePicker = persistedOverrideCloudId || sessionCloudId;

	// State for decoupled logic
	const [availableSites, setAvailableSites] = useState<AvailableSite[]>([]);
	const [selectedAvailableSite, setSelectedAvailableSite] = useState<AvailableSite | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	const onComplete = useCallback(
		({ availableSites: availableSitesFromResponse }: { availableSites: AvailableSite[] }) => {
			const firstSite = availableSitesFromResponse[0];
			const preferredSite = availableSitesFromResponse.find(
				(site: AvailableSite) => site.cloudId === overrideCloudIdForSitePicker,
			);
			setSelectedAvailableSite(preferredSite || firstSite);
			setAvailableSites(availableSitesFromResponse);
			setIsLoading(false);
			onSitesLoaded?.(availableSitesFromResponse);
		},
		[
			setAvailableSites,
			setIsLoading,
			onSitesLoaded,
			setSelectedAvailableSite,
			overrideCloudIdForSitePicker,
		],
	);

	const onErrorHandler = useCallback(
		(err: Error) => {
			setError(err);
			setIsLoading(false);
			onError?.(err);
		},
		[setError, setIsLoading, onError],
	);

	useGetFirstAvailableSiteDomain({
		preferredCloudId: overrideCloudIdForSitePicker,
		onComplete,
		onError: onErrorHandler,
	});

	return { availableSites, isLoading, error, selectedAvailableSite };
};
